import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { OnboardingService } from '../../services/onboarding.service';
import { Store } from '@ngrx/store';
import * as fromAuth from 'src/app/store/auth';
import { combineLatest } from 'rxjs';
import { map, filter, debounceTime, take } from 'rxjs/operators';
import { FeedbackService } from '../../services/feedback.service';
import { FeedbackPanelComponent } from '../../containers/feedback-panel/feedback-panel.component';
import dayjs from 'dayjs';
import { isSubwayCustomer } from 'src/app/modules/admin/utils/subway.utils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EnablePushPanelComponent } from '../enable-push-panel/enable-push-panel.component';
import { AuthService } from '../../../auth/services/auth.service';
import { UserHasPushSubscription } from '../../../auth/models/IGlimpseUser';
import { UserSettingsService } from '../../../shared/services/user-settings.service';

@UntilDestroy()
@Component({
    selector: 'app-full-layout',
    templateUrl: './full-layout.component.html',
    styleUrls: ['./full-layout.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FullLayoutComponent implements OnDestroy {
  user$ = this.store.select(fromAuth.selectUser);
  isImpersonated$ = this.store.select(fromAuth.selectIsImpersonated);

  shouldShowOnboarding$ = combineLatest([this.user$, this.onboarding.shown$]).pipe(
    filter(([user, _]) => !!user && user.customers.length > 0),
    map(([user, shown]) => !user.customers.every(cus => isSubwayCustomer(cus.id)) && !user.roles.includes('Auditor') && shown),
  );

  constructor(
    public onboarding: OnboardingService,
    public store: Store<{ auth: fromAuth.AuthState }>,
    feedback: FeedbackService,
    auth: AuthService,
    private settingsService: UserSettingsService,
    snackbar: MatSnackBar,
  ) {
    if (FeedbackService.ENABLED) {
      combineLatest([this.onboarding.shown$, this.isImpersonated$, feedback.getLastFeedback(), feedback.canAsk()])
        .pipe(untilDestroyed(this))
        .subscribe(([onboardingShown, impersonated, lastDate, canAskFeedback]) => {
          if (!impersonated && !onboardingShown && (lastDate === null || dayjs(lastDate).add(3, 'month').isBefore(dayjs())) && canAskFeedback) {
            setTimeout(() => {
              FeedbackPanelComponent.open(snackbar);
            }, 5000);
          }
        });
    }

    combineLatest([
      this.onboarding.shown$,
      this.settingsService.importantPopupShown$,
      this.isImpersonated$,
      auth.existingPushSubscription(),
      auth.pushDismissed(),
      this.user$,
    ])
      .pipe(
        untilDestroyed(this),
        debounceTime(7000),
        filter(([onboardingShown, popup, impersonated, _, dismissed, __]) => !onboardingShown && !popup && !impersonated && !dismissed),
        map(([, , , sub, , user]) => ({ sub, user })),
        take(1),
      )
      .subscribe(({ sub, user }) => {
        let hasThisSub = UserHasPushSubscription(user, sub);
        if (hasThisSub) return;
        this.settingsService.importantPopupShown$.next(true);
        EnablePushPanelComponent.open(snackbar)
          .afterDismissed()
          .pipe(take(1))
          .subscribe(() => this.settingsService.importantPopupShown$.next(false));
      });
    combineLatest([this.user$, this.isImpersonated$])
      .pipe(
        filter(([user, isImpersonated]) => !!user && !isImpersonated),
        take(1)
      )
      .subscribe(([user]) => this.store.dispatch(fromAuth.loadFeatures({ for: 'User', period: user.lastOnboardedAt })));
  }

  ngOnDestroy() {}
}
